import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Bot, Zap, Users, BookOpen, Trophy } from 'lucide-react';
import AvatarCircles from '../components/AvatarCircles/AvatarCircles';
import BentoGrid from '../components/BentoGrid/BentoGrid';
import BentoCard from '../components/BentoGrid/BentoCard';

const Home = () => {
  return (
    <div className="bg-gray-50 dark:bg-gray-950">
      {/* Hero Section */}
      <section className="relative bg-white dark:bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-5xl">
            <div className="inline-flex items-center gap-2 mb-8">
              <Zap size={20} className="text-primary-500" />
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                AI-Powered Learning Platform
              </span>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-black mb-4 leading-tight text-gray-900 dark:text-white">
              You need
              <span className="text-highlight ml-3">a powerful</span>
              <br />
              <span className="block mt-2">
                Python learning
              </span>
              <span className="block mt-2">
                platform.
              </span>
            </h1>
            <p className="text-xl md:text-2xl font-bold text-gray-600 dark:text-gray-400 mb-8">
              <span className="italic">Not a developer.</span> <span className="not-italic">😏</span>
            </p>
            
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-12 max-w-2xl leading-relaxed">
              Master Python 3 and build amazing chatbots with AI guidance. 
              Perfect for beginners aged 25+. In days.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Link 
                to="/register" 
                className="btn-primary text-lg inline-flex items-center justify-center"
              >
                Start Learning Free
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link 
                to="/courses" 
                className="btn-outline text-lg inline-flex items-center justify-center"
              >
                Browse Courses
              </Link>
            </div>
            
            {/* Social Proof */}
            <div className="flex items-center gap-6 flex-wrap">
              <AvatarCircles
                numPeople={99}
                avatarUrls={[
                  {
                    imageUrl: "https://avatars.githubusercontent.com/u/16860528",
                    profileUrl: "https://github.com/dillionverma",
                  },
                  {
                    imageUrl: "https://avatars.githubusercontent.com/u/1",
                    profileUrl: "https://github.com/octocat",
                  },
                  {
                    imageUrl: "https://avatars.githubusercontent.com/u/2",
                    profileUrl: "https://github.com/octocat2",
                  },
                  {
                    imageUrl: "https://avatars.githubusercontent.com/u/3",
                    profileUrl: "https://github.com/octocat3",
                  },
                ]}
              />
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">4.9</span>
                  <span className="text-gray-600 dark:text-gray-400">/ 5</span>
                  <span className="text-primary-500 ml-2">★★★★★</span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Loved by <strong>4,787+</strong> learners
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-28 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <h2 className="text-5xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
              The <span className="text-highlight">smarter way</span>
              <br />
              to learn Python.
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl">
              When coding stops being a problem and starts being your solution
            </p>
          </div>
          
          {/* Bento Grid */}
          <BentoGrid>
            {/* Feature 1 - Large horizontal */}
            <BentoCard
              name="Interactive Code Editor"
              Icon={Code}
              description="Write and run Python code directly in your browser with real-time feedback and AI assistance."
              href="/courses"
              cta="Start Coding"
              colSpan={2}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">01</span>
                  </div>
                </div>
              }
            />
            
            {/* Feature 2 - Tall */}
            <BentoCard
              name="AI-Powered Learning"
              Icon={Zap}
              description="Get instant help and explanations from Claude AI when you're stuck or need guidance. Available 24/7."
              href="/courses"
              cta="Get AI Help"
              colSpan={1}
              rowSpan={2}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">02</span>
                  </div>
                </div>
              }
            />
            
            {/* Feature 3 - Regular */}
            <BentoCard
              name="Build Chatbots"
              Icon={Bot}
              description="Create AI-powered chatbots with practical projects."
              href="/courses/build-chatbots"
              cta="Learn More"
              colSpan={1}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">03</span>
                  </div>
                </div>
              }
            />
            
            {/* Feature 4 - Regular */}
            <BentoCard
              name="Hands-On Projects"
              Icon={BookOpen}
              description="Build your portfolio with real-world projects."
              href="/courses"
              cta="View Projects"
              colSpan={1}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">04</span>
                  </div>
                </div>
              }
            />
            
            {/* Feature 5 - Regular */}
            <BentoCard
              name="Track Progress"
              Icon={Trophy}
              description="Monitor your journey with detailed tracking."
              href="/dashboard"
              cta="View Dashboard"
              colSpan={1}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">05</span>
                  </div>
                </div>
              }
            />
            
            {/* Feature 6 - Large horizontal */}
            <BentoCard
              name="Beginner-Friendly"
              Icon={Users}
              description="Designed specifically for adults 25+ with no prior coding experience. Start your journey today."
              href="/register"
              cta="Get Started"
              colSpan={2}
              background={
                <div className="absolute top-0 right-0 p-8">
                  <div className="w-12 h-12 rounded-full border-2 border-primary-500 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">06</span>
                  </div>
                </div>
              }
            />
          </BentoGrid>
        </div>
      </section>
      
      {/* Comparison Section */}
      <section className="py-28 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-black mb-8 text-gray-900 dark:text-white">
                Save money <span className="text-highlight">without</span> sacrificing quality
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                Hiring a tutor can be expensive, with each session adding to the cost. 
                Our platform gives you everything you need to master Python.
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white dark:bg-black rounded-2xl p-8 border-2 border-black dark:border-gray-800">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Traditional Tutor</div>
                <div className="text-4xl font-black text-gray-900 dark:text-white mb-4">$50+</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">per hour</div>
                <div className="mt-6 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div>• Limited availability</div>
                  <div>• Schedule conflicts</div>
                  <div>• Ongoing costs</div>
                </div>
              </div>
              
              <div className="bg-primary-500 rounded-2xl p-8 border-2 border-black dark:border-primary-600">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={16} className="text-black" />
                  <div className="text-sm font-semibold text-black">Our Platform</div>
                </div>
                <div className="text-4xl font-black text-black mb-4">$0</div>
                <div className="text-sm font-semibold text-black">100% Free</div>
                <div className="mt-6 space-y-2 text-sm font-medium text-black">
                  <div>✓ 24/7 AI assistance</div>
                  <div>✓ Learn at your pace</div>
                  <div>✓ Free forever</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 3 Steps Section */}
      <section className="py-28 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl md:text-6xl font-black mb-16 text-gray-900 dark:text-white">
            <span className="text-highlight">3 steps</span> for your success
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-black dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mb-6">
                <span className="text-2xl text-black">✓</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Pick a course</h3>
              <p className="text-gray-300 leading-relaxed">
                Browse our comprehensive Python courses and choose the perfect starting point for your learning journey.
              </p>
            </div>
            
            <div className="bg-black dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mb-6">
                <span className="text-2xl text-black">✓</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Learn & practice</h3>
              <p className="text-gray-300 leading-relaxed">
                Follow interactive lessons, write real code, and get instant AI-powered feedback as you progress.
              </p>
            </div>
            
            <div className="bg-black dark:bg-black rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
              <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mb-6">
                <span className="text-2xl text-black">✓</span>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Build projects</h3>
              <p className="text-gray-300 leading-relaxed">
                Apply your skills to real-world projects and build a portfolio that showcases your abilities.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Courses Section */}
      <section className="py-28 bg-white dark:bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white tracking-tight">
              Our Courses
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Comprehensive courses designed to take you from beginner to advanced
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Course 1 */}
            <div className="bg-black dark:bg-black rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200">
              <div className="h-56 bg-gray-900 dark:bg-gray-950 flex items-center justify-center relative">
                <Code size={80} className="text-primary-500" />
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-4 text-white">
                  Learn Python 3
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Master the basics of Python 3.12, one of the most powerful and in-demand 
                  programming languages today. Perfect for complete beginners.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="badge bg-gray-800 text-white">Beginner</span>
                  <span className="badge bg-primary-500 text-black">AI Assistance</span>
                  <span className="badge bg-gray-800 text-white">Projects</span>
                </div>
                <Link 
                  to="/courses/learn-python-3" 
                  className="btn-primary w-full text-center inline-block"
                >
                  Start Course
                </Link>
              </div>
            </div>
            
            {/* Course 2 */}
            <div className="bg-black dark:bg-black rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-primary-500 transition-all duration-200">
              <div className="h-56 bg-gray-900 dark:bg-gray-950 flex items-center justify-center relative">
                <Bot size={80} className="text-primary-500" />
              </div>
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-4 text-white">
                  Build Chatbots with Python
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Create intelligent chatbots using Python, NLP, and AI. Learn data science, 
                  machine learning, and deploy real-world conversational agents.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="badge bg-gray-800 text-white">Intermediate</span>
                  <span className="badge bg-primary-500 text-black">AI Assistance</span>
                  <span className="badge bg-gray-800 text-white">Machine Learning</span>
                </div>
                <Link 
                  to="/courses/build-chatbots" 
                  className="btn-primary w-full text-center inline-block"
                >
                  Start Course
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="relative py-28 bg-white dark:bg-gray-950 overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-7xl font-black mb-8 text-gray-900 dark:text-white">
            Ready to start <span className="text-highlight">learning</span>?
          </h2>
          <p className="text-xl md:text-2xl mb-12 text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join thousands of learners and start coding today. It's 100% free forever!
          </p>
          <Link 
            to="/register" 
            className="btn-primary text-lg inline-flex items-center justify-center"
          >
            Create Free Account
            <ArrowRight className="ml-2" size={22} />
          </Link>
          
          {/* Trust indicators */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-gray-900 dark:text-white">✓ No Credit Card Required</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">Start learning instantly</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-gray-900 dark:text-white">✓ 100% Free Forever</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">Complete access to all courses</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold mb-1 text-gray-900 dark:text-white">✓ AI Support Included</div>
              <div className="text-gray-600 dark:text-gray-400 text-sm">Get help whenever you need it</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;




